'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import * as tenantSettingsService from '@/lib/services/tenantSettings';
import { getApiErrorMessage } from '@/lib/apiError';

export function useTenantSettings(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: queryKeys.tenantSettings.current(),
    queryFn: () => tenantSettingsService.getTenantSettings(),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });

  return { ...query, errorMessage: getApiErrorMessage(query.error) };
}

export function useUpdateTenantSettings() {
  const mutation = useMutation({
    mutationFn: (request: tenantSettingsService.UpdateTenantSettingsRequest) =>
      tenantSettingsService.updateTenantSettings(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings.current() }),
  });

  return {
    ...mutation,
    errorMessage: getApiErrorMessage(mutation.error),
  };
}

export function useUploadTenantLogo() {
  const mutation = useMutation({
    mutationFn: (file: File) => tenantSettingsService.uploadTenantLogo(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.tenantSettings.current() }),
  });

  return {
    ...mutation,
    errorMessage: getApiErrorMessage(mutation.error),
  };
}

export function useAdvanceOptions() {
  const query = useQuery({
    queryKey: queryKeys.advanceOptions.current(),
    queryFn: () => tenantSettingsService.getAdvanceOptions(),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return { ...query, errorMessage: getApiErrorMessage(query.error) };
}

export function useUpdateAdvanceOptions() {
  const mutation = useMutation({
    mutationFn: (request: tenantSettingsService.UpdateAdvanceOptionsRequest) =>
      tenantSettingsService.updateAdvanceOptions(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.advanceOptions.current() }),
  });

  return {
    ...mutation,
    errorMessage: getApiErrorMessage(mutation.error),
  };
}

